import { ValueOf } from "@bridgesplit/utils";

import { FilterConfig, FilterReturnType, FilterState, FilterType } from "./types";

export function areAllFiltersEqual<T extends FilterConfig>(curr: FilterState<T>, prev: FilterState<T>) {
    for (const key of Object.keys(curr)) {
        if (!isFilterEqual(curr[key], prev[key])) return false;
    }
    return true;
}

export function matchesFilter<T extends FilterConfig>(
    config: T,
    filters: FilterState<T>,
    key: keyof T,
    value: string | string[] | boolean
): boolean {
    const type = config[key];

    if (type === FilterType.MultiSelect) {
        return matchesMultiSelectFilter(filters, key, value as string | string[]);
    }

    if (type === FilterType.Boolean) {
        return matchesBooleanFilter(filters, key, value as boolean);
    }

    // fallback to true (e.g. unrecognized type)
    return true;
}

export function matchesMultiSelectFilter<T extends FilterConfig>(
    filters: FilterState<T>,
    key: keyof T,
    value: string | string[]
): boolean {
    const enabled = filters[key].enabled;
    const condition = filters[key].condition;

    if (!enabled) return true;

    if (!(condition instanceof Set)) return false;

    if (typeof value === "string") return condition.has(value);
    return value.some((v) => condition.has(v));
}

export function matchesBooleanFilter<T extends FilterConfig>(
    filters: FilterState<T>,
    key: keyof T,
    value: boolean
): boolean {
    const enabled = filters[key].enabled;
    const condition = filters[key].condition;

    if (!enabled) return true;
    if (typeof value !== "boolean") return true;

    return value === condition;
}

export function isFilterEqual<T extends FilterConfig>(
    curr: ValueOf<FilterState<T>>,
    prev: ValueOf<FilterState<T>>
): boolean {
    const { enabled, condition } = curr;
    const { enabled: prevEnabled, condition: prevCondition } = prev;

    if (enabled !== prevEnabled) return false;

    // Always equal if not enabled
    if (!enabled && !prevEnabled) return true;

    return isConditionEqual(condition, prevCondition);
}

export function isConditionEqual<T extends FilterConfig>(
    curr: FilterReturnType[T[keyof T]],
    prev: FilterReturnType[T[keyof T]]
): boolean {
    if (curr instanceof Set && prev instanceof Set) {
        if (curr.size !== prev.size) return false;
        for (const val of curr) {
            if (!prev.has(val)) return false;
        }
        return true;
    }
    return curr === prev;
}

export function resetFilterIfAll<T extends FilterConfig, K extends FilterReturnType[T[keyof T]]>(
    curr: K,
    check: K,
    defaultState: { enabled: boolean; condition: K }
): { enabled: boolean; condition: K } {
    const isDefault = isConditionEqual(curr, check);
    if (isDefault && defaultState.condition instanceof Set) return defaultState;
    else
        return {
            enabled: true,
            condition: curr
        };
}
